// types/template.types.js
/**
 * Enums y constantes para Assistant Templates (Avatars)
 */

/**
 * @typedef {'es' | 'en'} TemplateLanguage
 * ISO 639-1 language codes
 */

/**
 * Enum de lenguajes disponibles para templates.
 * Valores son códigos ISO 639-1 que se envían al backend.
 */
export const TemplateLanguage = Object.freeze({
  SPANISH: 'es',
  ENGLISH: 'en',
});

/**
 * Labels para mostrar en la UI por cada lenguaje.
 */
export const TemplateLanguageLabel = Object.freeze({
  [TemplateLanguage.SPANISH]: 'Spanish (Español)',
  [TemplateLanguage.ENGLISH]: 'English',
});

/**
 * Lista ordenada de lenguajes para renderizar selects/dropdowns.
 */
export const TEMPLATE_LANGUAGE_OPTIONS = Object.values(TemplateLanguage).map((value) => ({
  value,
  label: TemplateLanguageLabel[value],
}));

/** Lenguaje por defecto */
export const DEFAULT_TEMPLATE_LANGUAGE = TemplateLanguage.SPANISH;

/**
 * @typedef {'normal' | 'json' | 'markdown' | 'html'} OutputFormatMode
 */

/**
 * Enum de modos de output format disponibles.
 */
export const OutputFormatMode = Object.freeze({
  NORMAL: 'normal',
  JSON: 'json',
  MARKDOWN: 'markdown',
  HTML: 'html',
});

/**
 * Labels para mostrar en la UI por cada output format.
 */
export const OutputFormatModeLabel = Object.freeze({
  [OutputFormatMode.NORMAL]: 'Normal',
  [OutputFormatMode.JSON]: 'JSON',
  [OutputFormatMode.MARKDOWN]: 'Markdown',
  [OutputFormatMode.HTML]: 'HTML',
});

/**
 * Descripciones para cada output format mode.
 */
export const OutputFormatModeDescription = Object.freeze({
  [OutputFormatMode.NORMAL]: 'Standard text output format',
  [OutputFormatMode.JSON]: 'Structured JSON output format',
  [OutputFormatMode.MARKDOWN]: 'Formatted markdown output',
  [OutputFormatMode.HTML]: 'HTML formatted output',
});

/**
 * Lista ordenada de output formats para renderizar selects/dropdowns.
 */
export const OUTPUT_FORMAT_OPTIONS = Object.values(OutputFormatMode).map((value) => ({
  value,
  label: OutputFormatModeLabel[value],
  description: OutputFormatModeDescription[value],
}));

/** Output format por defecto */
export const DEFAULT_OUTPUT_FORMAT = OutputFormatMode.NORMAL;

/**
 * Helper: construir objeto output_format para enviar al backend
 * @param {string} mode - El modo de output format
 * @param {Object} overrides - Overrides opcionales para la configuración
 * @returns {Object}
 */
export const buildOutputFormatConfig = (
  mode = DEFAULT_OUTPUT_FORMAT,
  overrides = {}
) => ({
  mode,
  strict_validation: true,
  pretty_print: false,
  indent: 2,
  provider_params: {},
  ...overrides,
});

/**
 * Helper: dado un value de language, retorna su label.
 * @param {string} language
 * @returns {string}
 */
export const getTemplateLanguageLabel = (language) =>
  TemplateLanguageLabel[language] || language || 'Unknown';

/**
 * Helper: dado un value de output format mode, retorna su label.
 * @param {string} mode
 * @returns {string}
 */
export const getOutputFormatModeLabel = (mode) =>
  OutputFormatModeLabel[mode] || mode || 'Unknown';

/**
 * @typedef {Object} AssistantTemplate
 * @property {string} id - UUID del template
 * @property {string} name - Nombre del template
 * @property {string} [description] - Descripción del template
 * @property {string} [avatar] - URL o base64 del avatar
 * @property {boolean} is_default - Si es template por defecto del sistema
 * @property {boolean} is_public - Si es template público (cualquiera puede usar)
 * @property {string} account_id - UUID de la cuenta propietaria
 * @property {string} [project_id] - UUID del proyecto (si es privado)
 * @property {string[]} [tags] - Tags para categorización
 * @property {TemplateLanguage} language - Lenguaje del template (default: 'es')
 * @property {Object} output_format - Configuración de formato de salida
 * @property {OutputFormatMode} output_format.mode - Modo de formato de salida
 * @property {boolean} output_format.strict_validation - Validación estricta
 * @property {boolean} output_format.pretty_print - Pretty print del output
 * @property {number} output_format.indent - Nivel de indentación
 * @property {Object} output_format.provider_params - Parámetros específicos del proveedor
 * @property {Array<Object>} sections - Secciones del template
 * @property {string} created_at - Timestamp de creación
 * @property {string} updated_at - Timestamp de última actualización
 */

/**
 * @typedef {Object} TemplateSection
 * @property {string} id - UUID de la sección
 * @property {string} template_id - UUID del template padre
 * @property {string} name - Nombre de la sección
 * @property {number} order - Orden de la sección
 * @property {string} content - Contenido markdown de la sección
 * @property {'section' | 'example'} section_type - Tipo de sección
 */
